import React, { useMemo } from "react";
import { WarningOutlined } from "@ant-design/icons";
import { Space, Table, Tag, Tooltip } from "antd";
import { type ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

import { MarketingLeadStatus, type MarketingLead } from "@fitness-app/data-models/entities/MarketingLead";

import { formatMarketingDateTime } from "../marketingFilters";
import { formatPln, toFiniteNumber } from "../marketingFormat";

interface Props {
  leads: MarketingLead[];
  loading: boolean;
  /** Hides the campaign column when the table is nested under a campaign row. */
  hideCampaignColumn?: boolean;
}

const EMPTY_VALUE = "—";
const DEFAULT_STATUS_META = { color: "blue", translationKey: "scheduled" };

const STATUS_META: Record<MarketingLeadStatus, { color: string; translationKey: string }> = {
  [MarketingLeadStatus.Showed]: { color: "green", translationKey: "showed" },
  [MarketingLeadStatus.NoShow]: { color: "red", translationKey: "noShow" },
  [MarketingLeadStatus.Canceled]: { color: "default", translationKey: "canceled" },
  [MarketingLeadStatus.Resigned]: { color: "volcano", translationKey: "resigned" },
  [MarketingLeadStatus.Scheduled]: DEFAULT_STATUS_META,
};

const AC_TAG_COLOR: Record<string, string> = {
  hot: "red",
  warm: "orange",
  cold: "blue",
  kupil: "green",
  kupił: "green",
};

const textOrEmptyValue = (value: string | null | undefined): string => value?.trim() || EMPTY_VALUE;

export const LeadsTable: React.FC<Props> = ({ leads, loading, hideCampaignColumn = false }) => {
  const { t } = useTranslation("dashboard");
  const columns = useMemo<ColumnsType<MarketingLead>>(
    () => [
      {
        title: t("marketingAnalytics.leads.columns.date"),
        dataIndex: "startTime",
        key: "startTime",
        width: 165,
        render: formatMarketingDateTime,
      },
      {
        title: t("marketingAnalytics.leads.columns.rep"),
        dataIndex: "assignedTo",
        key: "assignedTo",
        render: textOrEmptyValue,
      },
      {
        title: t("marketingAnalytics.leads.columns.campaign"),
        key: "campaign",
        render: (_, row) => (
          <Space size={4}>
            {row.utmCampaign ?? row.normalizedCampaignId ?? EMPTY_VALUE}
            {!row.campaignMatched ? (
              <Tooltip title={t("marketingAnalytics.leads.tooltips.unmatchedCampaign")}>
                <WarningOutlined
                  aria-label={t("marketingAnalytics.leads.tooltips.unmatchedCampaign")}
                  className="text-amber-500"
                />
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      {
        title: t("marketingAnalytics.leads.columns.budget"),
        dataIndex: "budgetAnswer",
        key: "budgetAnswer",
        render: textOrEmptyValue,
      },
      {
        title: t("marketingAnalytics.leads.columns.terminOk"),
        dataIndex: "terminOk",
        key: "terminOk",
        render: textOrEmptyValue,
      },
      {
        title: t("marketingAnalytics.leads.columns.status"),
        dataIndex: "status",
        key: "status",
        render: (status: MarketingLeadStatus) => {
          const meta = STATUS_META[status] ?? DEFAULT_STATUS_META;
          return <Tag color={meta.color}>{t(`marketingAnalytics.leads.status.${meta.translationKey}`)}</Tag>;
        },
      },
      {
        title: t("marketingAnalytics.leads.columns.acTags"),
        dataIndex: "acTags",
        key: "acTags",
        render: (tags: string[] | null) => {
          const uniqueTags = Array.from(new Set(tags?.filter(Boolean) ?? []));

          return uniqueTags.length ? (
            <Space size={[0, 4]} wrap>
              {uniqueTags.map((tag) => (
                <Tag key={tag} color={AC_TAG_COLOR[tag.toLowerCase()] ?? "default"}>
                  {tag}
                </Tag>
              ))}
            </Space>
          ) : (
            EMPTY_VALUE
          );
        },
      },
      {
        title: t("marketingAnalytics.leads.columns.acFollowUp"),
        dataIndex: "acFollowUp",
        key: "acFollowUp",
        render: textOrEmptyValue,
      },
      {
        title: t("marketingAnalytics.leads.columns.acNoReason"),
        dataIndex: "acNoReason",
        key: "acNoReason",
        render: textOrEmptyValue,
      },
      {
        title: t("marketingAnalytics.leads.columns.sale"),
        dataIndex: "sale",
        key: "sale",
        render: (sale: boolean) => (
          <Tag color={sale ? "green" : "default"}>{t(`marketingAnalytics.leads.sale.${sale ? "yes" : "no"}`)}</Tag>
        ),
      },
      {
        title: t("marketingAnalytics.leads.columns.productContract"),
        key: "product",
        render: (_, row) => {
          const contractValue = toFiniteNumber(row.contractValue);

          if (!row.product && contractValue == null) {
            return EMPTY_VALUE;
          }

          return (
            <Space size={4}>
              {row.product ? <span className="font-medium">{row.product}</span> : null}
              {contractValue != null ? (
                <span className="tabular-nums text-gray-500">{formatPln(contractValue)}</span>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: t("marketingAnalytics.leads.columns.conversionWindow"),
        dataIndex: "conversionWindowOpen",
        key: "conversionWindowOpen",
        render: (isOpen: boolean) => (
          <Tag color={isOpen ? "blue" : "default"}>
            {t(`marketingAnalytics.leads.conversionWindow.${isOpen ? "open" : "closed"}`)}
          </Tag>
        ),
      },
      {
        title: t("marketingAnalytics.leads.columns.hyros"),
        key: "hyros",
        render: (_, row) => {
          if (!row.sale) {
            return EMPTY_VALUE;
          }

          const discrepancy = toFiniteNumber(row.hyrosDiscrepancy);
          const hasDiscrepancy = discrepancy != null && discrepancy !== 0;
          const matchKey = row.hyrosMatch === true ? "matched" : row.hyrosMatch === false ? "missing" : "unknown";
          const matchColor = row.hyrosMatch === true ? "green" : row.hyrosMatch === false ? "red" : "default";
          const discrepancyMessage = t<string>("marketingAnalytics.leads.tooltips.hyrosDiscrepancy", {
            amount: formatPln(discrepancy),
          });

          return (
            <Space size={4}>
              <Tag color={matchColor}>{t(`marketingAnalytics.leads.hyros.${matchKey}`)}</Tag>
              {hasDiscrepancy ? (
                <Tooltip title={discrepancyMessage}>
                  <WarningOutlined aria-label={discrepancyMessage} className="text-amber-500" />
                </Tooltip>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: t("marketingAnalytics.leads.columns.hyrosSource"),
        key: "attribution",
        render: (_, row) => row.lastSource ?? row.originSource ?? EMPTY_VALUE,
      },
    ],
    [t],
  );
  const visibleColumns = useMemo(
    () => (hideCampaignColumn ? columns.filter((column) => column.key !== "campaign") : columns),
    [columns, hideCampaignColumn],
  );

  return (
    <Table<MarketingLead>
      rowKey="id"
      columns={visibleColumns}
      dataSource={leads}
      loading={loading}
      scroll={{ x: hideCampaignColumn ? 2000 : 2200 }}
      size="middle"
      pagination={{ pageSize: 50, showSizeChanger: true }}
    />
  );
};
